import feFunctions from '../../services/fe-functions';
import { FORM_ROUTE_STATES } from '../../services/form-engine.service';

// Client-side half of the inactivity fix (report 2.8): a builder session left
// open and unattended is signed out after IDLE_LOGOUT_MS. This only covers
// browsers that actually run this code — the server-side backstop in
// auth-session.service.ts (MAX_IDLE_MS) covers stolen/replayed tokens and
// non-browser callers. Never runs for completers (no Firebase user).
//
// Tabs share one Firebase user and sessionId. Activity is written to
// localStorage (keyed by uid) so a background tab does not idle-logout an
// active tab. Same try/catch pattern as template-list folder storage.
const ACTIVITY_EVENTS = ["mousemove", "keydown", "mousedown", "click", "scroll", "touchstart"];
// 30 days without input (matches MAX_IDLE_MS on the server). The warning shows
// only in the last 5 minutes.
const IDLE_LOGOUT_MS = 30 * 24 * 60 * 60 * 1000;
const IDLE_WARNING_MS = IDLE_LOGOUT_MS - 5 * 60 * 1000;
const CHECK_INTERVAL_MS = 15 * 1000;
const ACTIVITY_THROTTLE_MS = 1000;
const HEARTBEAT_MS = 5 * 60 * 1000;
const STORAGE_KEY_PREFIX = "fe.idle.lastActivityAt.";
const HEARTBEAT_KEY_PREFIX = "fe.idle.lastHeartbeat.";

export default class IdleSessionService {
  constructor(firebase, $interval, $window, $state, AuthService, FlashService) {
    'ngInject';
    Object.assign(this, { firebase, $interval, $window, $state, AuthService, FlashService });
    this._lastActivityAt = Date.now();
    this._lastRecordedAt = 0;
    this._lastHeartbeatAt = 0;
    this._warned = false;
    this._loggingOut = false;
    this._heartbeatInFlight = false;
    this._checkPromise = null;
    this._onActivity = this._onActivity.bind(this);
    this._onStorage = this._onStorage.bind(this);
  }

  // Called once from the module .run() block.
  start() {
    this.$window.addEventListener("storage", this._onStorage);
    this.firebase.auth().onAuthStateChanged((user) => {
      if (user) this._beginMonitoring();
      else this._stopMonitoring();
    });
  }

  _localStorage() {
    try {
      return this.$window.localStorage;
    } catch (_e) {
      return null;
    }
  }

  _storageKey() {
    const uid = this.firebase.auth().currentUser?.uid;
    return uid ? STORAGE_KEY_PREFIX + uid : null;
  }

  _heartbeatKey() {
    const uid = this.firebase.auth().currentUser?.uid;
    return uid ? HEARTBEAT_KEY_PREFIX + uid : null;
  }

  _storageGet(key) {
    const store = this._localStorage();
    if (!store || !key) return null;
    try {
      return store.getItem(key);
    } catch (_e) {
      return null;
    }
  }

  async _maybeHeartbeat(idleFor) {
    // Only while the user still looks active in this browser. auth({ heartbeat: true })
    // runs assertLiveSession inside handleAuth (the callable otherwise skips it).
    if (idleFor >= HEARTBEAT_MS) return;
    if (this._heartbeatInFlight || this._loggingOut) return;
    const now = Date.now();
    if (now - this._lastHeartbeatAt < HEARTBEAT_MS) return;
    const key = this._heartbeatKey();
    const lastShared = Number(this._storageGet(key));
    if (Number.isFinite(lastShared) && now - lastShared < HEARTBEAT_MS) return;
    this._heartbeatInFlight = true;
    try {
      await feFunctions(this.firebase).httpsCallable("auth")({ heartbeat: true });
      this._lastHeartbeatAt = Date.now();
      const store = this._localStorage();
      if (store && key) {
        try {
          store.setItem(key, String(this._lastHeartbeatAt));
        } catch (_e) { /* private mode */ }
      }
    } catch (err) {
      console.error("Session heartbeat failed", err);
      if (err?.details?.logout) await this._logoutFromIdle(err);
    } finally {
      this._heartbeatInFlight = false;
    }
  }

  async _logoutFromIdle(error) {
    if (this._loggingOut) return;
    this._loggingOut = true;
    try {
      // On a form page, end the builder session but stay on the form: it keeps
      // working as a completer, and navigating to login would discard entries.
      await this.AuthService.logout(this._onFormRoute(), error);
    } catch (err) {
      // Revoke failed: stay signed in and watch again so logout can retry.
      console.error("Idle logout failed", err);
      this._loggingOut = false;
    }
  }

  _readSharedActivity() {
    const n = Number(this._storageGet(this._storageKey()));
    return Number.isFinite(n) ? n : 0;
  }

  _writeSharedActivity(at) {
    const store = this._localStorage();
    const key = this._storageKey();
    if (!store || !key) return;
    try {
      store.setItem(key, String(at));
    } catch (_e) {
      // Quota / private mode after the getter succeeded.
    }
  }

  _syncFromShared() {
    const shared = this._readSharedActivity();
    if (shared > this._lastActivityAt) {
      this._lastActivityAt = shared;
      this._dismissIdleWarning();
    }
  }

  _onStorage(ev) {
    const key = this._storageKey();
    if (!key || ev.key !== key) return;
    this._syncFromShared();
  }

  _onActivity() {
    const now = Date.now();
    this._dismissIdleWarning();
    if (now - this._lastRecordedAt < ACTIVITY_THROTTLE_MS) return;
    this._lastRecordedAt = now;
    this._lastActivityAt = now;
    this._writeSharedActivity(now);
  }

  _onFormRoute() {
    return FORM_ROUTE_STATES.includes(this.$state?.current?.name || '');
  }

  _dismissIdleWarning() {
    this._warned = false;
    $('#alert-flyover .idle-session-warning').remove();
  }

  _beginMonitoring() {
    if (this._checkPromise) return; // already monitoring
    const now = Date.now();
    this._lastActivityAt = now;
    this._warned = false;
    this._loggingOut = false;
    this._heartbeatInFlight = false;
    this._lastHeartbeatAt = 0;
    this._syncFromShared();
    this._writeSharedActivity(this._lastActivityAt);
    ACTIVITY_EVENTS.forEach((evt) => this.$window.addEventListener(evt, this._onActivity, { passive: true }));
    this._checkPromise = this.$interval(() => this._checkIdle(), CHECK_INTERVAL_MS);
  }

  _stopMonitoring() {
    this._loggingOut = false;
    this._dismissIdleWarning();
    ACTIVITY_EVENTS.forEach((evt) => this.$window.removeEventListener(evt, this._onActivity));
    if (this._checkPromise) {
      this.$interval.cancel(this._checkPromise);
      this._checkPromise = null;
    }
  }

  async _checkIdle() {
    if (this._loggingOut) return;
    this._syncFromShared();
    const idleFor = Date.now() - this._lastActivityAt;
    await this._maybeHeartbeat(idleFor);

    if (idleFor >= IDLE_LOGOUT_MS) {
      await this._logoutFromIdle({
        details: { validationErrorMessage: "You were signed out due to inactivity. Please sign in again." }
      });
      return;
    }

    if (idleFor >= IDLE_WARNING_MS && !this._warned && !this._onFormRoute()) {
      this._warned = true;
      const minutesLeft = Math.ceil((IDLE_LOGOUT_MS - idleFor) / 60000);
      this.FlashService.warning(
        `You'll be signed out due to inactivity in about ${minutesLeft} minute${minutesLeft === 1 ? "" : "s"}. Move the mouse or press a key to stay signed in.`,
        -1
      );
      $('#alert-flyover > div.warning').last().addClass('idle-session-warning');
    }
  }
}
